import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

/**
 * Detects Supabase auth errors in URL hash (from magic links, etc)
 * and redirects to appropriate error pages.
 * 
 * Supabase returns errors like:
 * #error=access_denied&error_code=otp_expired&error_description=Email+link+is+invalid+or+has+expired
 */
export function AuthErrorHandler({ children }: { children: React.ReactNode }) {
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        // Check URL hash for auth errors
        const hash = window.location.hash;
        if (!hash || !hash.includes('error=')) {
            return;
        }

        console.log('[AuthErrorHandler] Detected auth error in URL hash:', hash);

        // Parse hash params
        const hashParams = new URLSearchParams(hash.substring(1));
        const error = hashParams.get('error');
        const errorCode = hashParams.get('error_code');
        const errorDescription = hashParams.get('error_description');

        console.log('[AuthErrorHandler] Error details:', { error, errorCode, errorDescription });

        // Check for expired magic link
        if (
            errorCode === 'otp_expired' ||
            errorDescription?.toLowerCase().includes('expired') ||
            errorDescription?.toLowerCase().includes('invalid')
        ) {
            console.log('[AuthErrorHandler] Magic link expired, redirecting to /magic-link-expired');
            // Clear the hash and redirect
            window.location.hash = '';
            navigate('/magic-link-expired', { replace: true });
            return;
        }

        // Other auth errors - could add more handlers here
        if (error === 'access_denied') {
            console.log('[AuthErrorHandler] Access denied, redirecting to login');
            window.location.hash = '';
            navigate('/login', { replace: true, state: { error: errorDescription } });
            return;
        }
    }, [location.hash, navigate]);

    return <>{children}</>;
}
